import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, DatePicker, message, Table } from "antd";
import dayjs, { Dayjs } from "dayjs";
import styled from "styled-components";
import { PageHeader } from "@ant-design/pro-components";
import { ColumnType } from "antd/es/table";
import TableToolbar from "../../components/TableToolbar";
import SeasonService from "../../services/season.service";
import { PLSeason } from "../../types";
import { useGetCompanies } from "../../hooks/useGetCompanies";
import SeasonDetails from "./SeasonDetails";

const routes = {
  items: [{ title: "Seasons" }, { title: "List" }],
};

const SPageHeader = styled(PageHeader)`
  border-bottom: 1px solid rgb(235, 237, 240);
  background: #fff;
`;

const SPageContent = styled.div`
  margin: 24px 24px 0;
  padding: inherit;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// validFrom/validTo are stored as UTC-midnight instants (see the AGM date picker below for why)
// and the active season's validTo is a far-future sentinel rather than a real end date
const formatSeasonBoundaryDate = (isoDate?: string) => {
  if (!isoDate) {
    return "-";
  }

  if (isoDate.slice(0, 4) === "9999") {
    return "Ongoing";
  }

  // parse just the calendar date, not the full UTC instant, to stay correct across timezones
  return dayjs(isoDate.slice(0, 10)).format("D MMM YYYY");
};

// A season's AGM must take place between the 1st of May and the 30th of June
// of the season's first calendar year (e.g. for "Premier League 2026/27" that's May-June 2026)
const getAgmDateRange = (season: PLSeason): [Dayjs, Dayjs] | null => {
  const match = season.label.match(/(\d{4})\/\d{2}/);

  if (!match) {
    return null;
  }

  const year = match[1];

  return [
    dayjs(`${year}-05-01`).startOf("day"),
    dayjs(`${year}-06-30`).endOf("day"),
  ];
};

const SeasonList = () => {
  const seasonService = useMemo(() => new SeasonService(), []);
  const { companies } = useGetCompanies({ onlyClubs: true });

  const [seasons, setSeasons] = useState<PLSeason[]>([]);
  const [loading, setLoading] = useState(false);
  const [agmDates, setAgmDates] = useState<Record<string, Dayjs | null>>({});
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const fetchSeasons = async () => {
    setLoading(true);
    const seasons = await seasonService.getSeasons();

    if (seasons && seasons.data) {
      setSeasons(seasons.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSeasons();
  }, []);

  // seasons are sorted label desc (most recent first), so a season "has a next season"
  // (i.e. isn't the current/active one) whenever there's a row above it in the list
  const hasNextSeason = (season: PLSeason) => {
    const index = seasons.findIndex((s) => s.id === season.id);
    return index > 0;
  };

  const handleAgmDateChange = async (season: PLSeason, date: Dayjs | null) => {
    if (!date && hasNextSeason(season)) {
      message.error(
        `The AGM date for ${season.label} can't be cleared, since a later season already exists.`,
      );
      return;
    }

    const range = getAgmDateRange(season);

    if (date && range && (date.isBefore(range[0]) || date.isAfter(range[1]))) {
      message.error(
        `The AGM date for ${season.label} must be between ${range[0].format(
          "D MMM YYYY",
        )} and ${range[1].format("D MMM YYYY")}.`,
      );
      return;
    }

    setAgmDates((prev) => ({ ...prev, [season.id]: date }));

    try {
      // saved automatically on change - no save button needed
      await seasonService.updateSeasonAgmDate(season.id, date);
      message.success(
        date
          ? `AGM date for ${season.label} saved as ${date.format(
              "D MMM YYYY",
            )}.`
          : `AGM date for ${season.label} cleared.`,
      );
      await fetchSeasons();
    } catch (err) {
      message.error(`Failed to save the AGM date for ${season.label}.`);
    }
  };

  const columns: ColumnType<PLSeason>[] = [
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Valid From",
      dataIndex: "validFrom",
      key: "validFrom",
      render: (validFrom: string) => formatSeasonBoundaryDate(validFrom),
    },
    {
      title: "Valid To",
      dataIndex: "validTo",
      key: "validTo",
      render: (validTo: string) => formatSeasonBoundaryDate(validTo),
    },
    {
      title: "AGM Date",
      dataIndex: "agmDate",
      key: "agmDate",
      onCell: () => ({
        // prevent the row's expand-on-click from firing when using the picker
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
      }),
      render: (agmDate: string, season: PLSeason) => {
        const range = getAgmDateRange(season);

        return (
          <DatePicker
            value={
              agmDates[season.id] !== undefined
                ? agmDates[season.id]
                : agmDate
                ? // parse just the calendar date (not the full UTC instant) so the picker shows
                  // the date that was saved regardless of the viewer's local timezone offset
                  dayjs(agmDate.slice(0, 10))
                : null
            }
            disabledDate={(current) =>
              !!range &&
              (current.isBefore(range[0], "day") ||
                current.isAfter(range[1], "day"))
            }
            // only the active season's AGM date can be cleared - see handleAgmDateChange
            allowClear={!hasNextSeason(season)}
            onChange={(date) => handleAgmDateChange(season, date)}
          />
        );
      },
    },
  ];

  return (
    <>
      <SPageHeader title="Manage Seasons" breadcrumb={routes} />
      <SPageContent>
        <Alert
          message="How season validity works"
          description={
            <>
              Setting a season's AGM date marks it as that season's start (Valid
              From) and the previous season's end (Valid To) - so there are no
              gaps between seasons. Only the current season's AGM date can be
              cleared - doing so makes the previous season show as "Ongoing"
              again until a new AGM date is set. Earlier seasons' AGM dates can
              no longer be cleared once a later season exists.
            </>
          }
          type="info"
          showIcon
        />
        <Card bodyStyle={{ paddingTop: "0" }}>
          <TableToolbar title="Premier League seasons" />
          <Table
            rowKey={(record) => record.id}
            columns={columns}
            dataSource={seasons}
            loading={loading}
            pagination={false}
            expandable={{
              expandRowByClick: true,
              expandedRowKeys,
              onExpandedRowsChange: (keys) => {
                const keysArray = keys as string[];
                setExpandedRowKeys(
                  keysArray.length ? [keysArray[keysArray.length - 1]] : [],
                );
              },
              expandedRowRender: (season, index) => (
                <SeasonDetails
                  season={season}
                  // seasons are sorted label desc (most recent first), so the
                  // chronologically-next season sits at the row above (index - 1)
                  nextSeason={seasons[index - 1]}
                  companies={companies}
                />
              ),
            }}
          />
        </Card>
      </SPageContent>
    </>
  );
};

export default SeasonList;
